import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CloudDownloadIcon from "@mui/icons-material/CloudDownloadOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import useApiCall from "../../hooks/useApiCall";
import useFetch from "../../hooks/useFetch";
import SearchInput from "../../components/SearchInput";
import LoadingSpinner from "../../components/LoadingSpinner";
import { INDIGO, SLATE } from "../../constants/Colors";

/**
 * Right-side sidebar for browsing backoffice intents by market and generating
 * a prepopulated Intent Model from one at a time via LLM.
 *
 * Backoffice intents are keyed by `intent_name` (no separate id); the
 * human-readable label is `internal_intent_name`. Intents that already exist
 * in Configurator (matched by intent_name) are filtered out entirely — only
 * importable intents are shown.
 *
 * On generate, the sidebar hides (via onClose) without resetting its market/
 * search state, since IntentModelsList reopens it once the prefilled New
 * Intent form is saved — the existing-intents list is refreshed on every
 * reopen so the just-created intent no longer shows as importable.
 */
export default function ImportIntentsSidebar({ isOpen, onClose, tokens, onGenerated }) {
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedNames, setExpandedNames] = useState([]);
  const [generatingName, setGeneratingName] = useState(null);
  const { makeRequest } = useApiCall();

  const { data: appsData, loading: marketsLoading } = useFetch("/v1/apps?status=active", tokens, []);
  const { data: existingData, refresh: refreshExisting } = useFetch("/v1/intent_models", tokens, []);

  useEffect(() => {
    if (isOpen) {
      refreshExisting();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    data: backofficeData,
    loading: intentsLoading,
    error: intentsError,
    refresh: refreshIntents,
  } = useFetch(
    `/v1/intent_models/backoffice_intents?market=${selectedMarket}`,
    tokens,
    [selectedMarket],
    {},
    !!selectedMarket
  );

  const markets = useMemo(() => {
    const list = Array.isArray(appsData) ? appsData : [];
    const values = list.map((app) => app.market).filter(Boolean);
    return Array.from(new Set(values)).sort();
  }, [appsData]);

  const existingIntentNames = useMemo(() => {
    return new Set((existingData?.intent_models || []).map((m) => m.intent_name));
  }, [existingData]);

  const backofficeIntents = useMemo(() => backofficeData?.intents || [], [backofficeData]);

  const filteredIntents = useMemo(() => {
    if (!searchQuery.trim()) return backofficeIntents;
    const q = searchQuery.toLowerCase();
    return backofficeIntents.filter(
      (i) =>
        i.intent_name?.toLowerCase().includes(q) ||
        i.internal_intent_name?.toLowerCase().includes(q)
    );
  }, [backofficeIntents, searchQuery]);

  const importableIntents = useMemo(
    () => filteredIntents.filter((i) => !existingIntentNames.has(i.intent_name)),
    [filteredIntents, existingIntentNames]
  );

  const handleMarketChange = (_, value) => {
    setSelectedMarket(value);
    setSearchQuery("");
    setExpandedNames([]);
  };

  const toggleExpand = (intentName) => {
    setExpandedNames((prev) =>
      prev.includes(intentName) ? prev.filter((x) => x !== intentName) : [...prev, intentName]
    );
  };

  const handleClose = () => {
    onClose();
    setSelectedMarket(null);
    setSearchQuery("");
    setExpandedNames([]);
  };

  const handleGenerate = async (intent) => {
    setGeneratingName(intent.intent_name);
    try {
      const response = await makeRequest("/v1/intent_models/generate_llm_intent", {
        method: "POST",
        body: {
          intents: [
            {
              intent_name: intent.intent_name,
              internal_intent_name: intent.internal_intent_name,
              sentences: intent.sentences || [],
            },
          ],
        },
        authTokens: tokens,
      });
      const generated = response?.intent_models?.[0];
      if (generated) {
        onGenerated(generated, selectedMarket);
        // Close without resetting local state (market/search), so the sidebar
        // reopens showing the same market once the New Intent form is saved.
        onClose();
      }
    } catch {
      // error already toasted by useApiCall
    } finally {
      setGeneratingName(null);
    }
  };

  const renderIntentRow = (intent, isLast) => {
    const isExpanded = expandedNames.includes(intent.intent_name);
    const isGenerating = generatingName === intent.intent_name;

    return (
      <Box
        key={intent.intent_name}
        sx={{
          borderBottom: !isLast || isExpanded ? `1px solid ${SLATE[100]}` : "none",
          "&:hover .generate-btn": { visibility: "visible" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, px: 2, py: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 500,
                color: SLATE[900],
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {intent.intent_name}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: SLATE[400] }}>
              {intent.internal_intent_name ? `${intent.internal_intent_name} · ` : ""}
              {(intent.sentences || []).length} sentences
            </Typography>
          </Box>

          <Button
            className="generate-btn"
            size="small"
            variant="outlined"
            startIcon={isGenerating ? <CircularProgress size={12} /> : <AutoAwesomeIcon sx={{ fontSize: "0.9rem" }} />}
            onClick={() => handleGenerate(intent)}
            disabled={isGenerating}
            sx={{
              visibility: "hidden",
              flexShrink: 0,
              textTransform: "none",
              fontSize: "0.75rem",
              borderColor: INDIGO[400],
              color: INDIGO[600],
              "&:hover": { borderColor: INDIGO[500], bgcolor: INDIGO[50] },
            }}
          >
            Generate LLM Intent
          </Button>

          <IconButton
            size="small"
            aria-label={isExpanded ? "Collapse sentences" : "Expand sentences"}
            onClick={() => toggleExpand(intent.intent_name)}
            sx={{ color: SLATE[400], flexShrink: 0 }}
          >
            {isExpanded ? <KeyboardArrowUpIcon sx={{ fontSize: "1.1rem" }} /> : <KeyboardArrowDownIcon sx={{ fontSize: "1.1rem" }} />}
          </IconButton>
        </Box>

        {isExpanded && (
          <Box sx={{ px: 2, pb: 1.5, pl: 4, bgcolor: SLATE[50] }}>
            {(intent.sentences || []).map((sentence, i) => (
              <Typography key={i} sx={{ fontSize: "0.8125rem", color: SLATE[600], mb: 0.5 }}>
                "{sentence}"
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleClose}
      sx={{ zIndex: 21474834 }}
      PaperProps={{
        sx: {
          width: "55%",
          minWidth: 560,
          maxWidth: 820,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: SLATE[50],
        },
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          bgcolor: "#ffffff",
          borderBottom: `1px solid ${SLATE[200]}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <CloudDownloadIcon sx={{ fontSize: "1.25rem", color: INDIGO[500] }} />
          <Box>
            <Typography sx={{ fontSize: "0.6875rem", fontWeight: 600, color: SLATE[400], textTransform: "uppercase", letterSpacing: "0.08em", mb: "2px" }}>
              AI Models
            </Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: SLATE[900] }}>
              Import Intent
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={handleClose} sx={{ color: SLATE[400], "&:hover": { color: SLATE[700], bgcolor: SLATE[100] } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box sx={{ p: 3, pb: 2, display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
          <Autocomplete
            options={markets}
            value={selectedMarket}
            onChange={handleMarketChange}
            loading={marketsLoading}
            componentsProps={{ popper: { sx: { zIndex: 21474836 } } }}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Market" placeholder="Select a market…" />
            )}
          />

          {selectedMarket && (
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search intent name…"
              minWidth="100%"
              sx={{ width: "100%" }}
            />
          )}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 3, pb: 3 }}>
          {!selectedMarket ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
                Choose a market to see its importable intents.
              </Typography>
            </Box>
          ) : intentsLoading ? (
            <LoadingSpinner message="Loading backoffice intents..." />
          ) : intentsError ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[500], mb: 2 }}>
                Couldn't load intents for this market.
              </Typography>
              <Button variant="outlined" size="small" onClick={refreshIntents} sx={{ textTransform: "none" }}>
                Retry
              </Button>
            </Box>
          ) : importableIntents.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography sx={{ fontSize: "0.875rem", color: SLATE[500] }}>
                No importable intents found for this market.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "12px", overflow: "hidden" }}>
              {importableIntents.map((intent, idx) =>
                renderIntentRow(intent, idx === importableIntents.length - 1)
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
